// ChipRemovable — generic pill with optional leading element, label, optional
// muted suffix, and a required remove × button.
//
// Mirrors `.recip-chip` in
// /Users/harry/Downloads/employee-portal/project/styles.css (lines 1916–1930).
// Slightly tighter than `.ra-recip-chip` — smaller font and a narrower padding.
//
// Use this when the leading element is not necessarily an Avatar — e.g. an
// AppLogo or a plain icon. For person/group recipients in the bulk-request flow
// reach for RecipientChip instead.

import { Box } from "@chakra-ui/react";
import { ReactNode, forwardRef } from "react";
import { TbX } from "react-icons/tb";

export interface ChipRemovableProps {
  /** Optional leading element — Avatar, AppLogo, icon, etc. */
  leftEl?: ReactNode;
  /** Required label text. */
  label: string;
  /** Muted suffix shown after the label (e.g. "(Myself)"). */
  suffix?: string;
  /** Required remove handler — renders the × button. */
  onRemove: () => void;
}

const ChipRemovable = forwardRef<HTMLSpanElement, ChipRemovableProps>(
  ({ leftEl, label, suffix, onRemove }, ref) => {
    return (
      <Box
        as="span"
        ref={ref}
        display="inline-flex"
        alignItems="center"
        gap="1xs" // 6px
        // .recip-chip padding: 4px 12px 4px <leftEl ? 4px : 8px>
        // When there's a leftEl with visual mass we tuck it tighter on the left;
        // pure-text chips get a balanced 8px left pad.
        padding={leftEl ? "4px 12px 4px 4px" : "4px 12px 4px 8px"}
        bg="neutral.0"
        borderWidth="1px"
        borderStyle="solid"
        borderColor="neutral.200"
        borderRadius="full"
        fontSize="smd" // 13px
        color="neutral.950"
        transition="border-color 120ms"
        _hover={{ borderColor: "neutral.300" }}
      >
        {leftEl && (
          <Box as="span" display="inline-flex" alignItems="center" flexShrink={0}>
            {leftEl}
          </Box>
        )}

        {/* .recip-chip__name */}
        <Box as="span" fontWeight={500} color="neutral.950">
          {label}
        </Box>

        {/* .recip-chip__myself — muted suffix */}
        {suffix && (
          <Box as="span" color="neutral.600" fontWeight={400}>
            {suffix}
          </Box>
        )}

        {/* Remove × — matches the .ra-recip-chip__remove footprint (18×18) */}
        <Box
          as="button"
          type="button"
          aria-label={`Remove ${label}`}
          onClick={onRemove}
          width="18px"
          height="18px"
          borderRadius="full"
          display="inline-flex"
          alignItems="center"
          justifyContent="center"
          color="neutral.500"
          bg="transparent"
          border="none"
          cursor="pointer"
          ml="2xs" // 4px
          transition="background-color 120ms, color 120ms"
          _hover={{ bg: "neutral.100", color: "neutral.900" }}
        >
          <TbX size={12} />
        </Box>
      </Box>
    );
  },
);

ChipRemovable.displayName = "ChipRemovable";

export default ChipRemovable;
